import React, { useState } from "react";
import { toast } from "sonner";
import { Download, FileText, History, ShieldAlert, ShieldCheck, ShieldQuestion, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FileUploadDialog } from "@/components/platform/FileUploadDialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError, fmtBytes, fmtDT } from "@/lib/platform";

const INTEGRITY = {
  verified: { icon: ShieldCheck, cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Verified" },
  failed: { icon: ShieldAlert, cls: "text-red-700 bg-red-50 border-red-200", label: "Integrity failed" },
  unverified: { icon: ShieldQuestion, cls: "text-slate-600 bg-slate-50 border-slate-200", label: "Unverified" },
};

export const downloadVersion = async (fileId, v) => {
  try {
    const res = await api.get(`/platform/files/${fileId}/versions/${v.version}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = v.filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Download failed");
  }
};

export const IntegrityBadge = ({ status, testId }) => {
  const s = INTEGRITY[status] || INTEGRITY.unverified;
  const Icon = s.icon;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
};

export const FileObjectCard = ({ file, onChanged }) => {
  const auth = useAuth();
  const [showVersions, setShowVersions] = useState(false);
  const [upload, setUpload] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const versions = [...(file.versions || [])].sort((a, b) => b.version - a.version);

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post(`/platform/files/${file.id}/verify`);
      data.status === "verified" ? toast.success("All versions match their SHA-256 fingerprints")
        : toast.error(`Integrity check: ${data.status}`);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e, "Verification failed"));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl bg-white p-4" data-testid={`file-card-${file.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
          <FileText className="w-4 h-4 text-slate-500" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[#003366] truncate" data-testid={`file-name-${file.id}`}>{file.name}</div>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
              {file.source === "evidence_vault" ? "Evidence Vault" : file.module}
            </span>
            <span className="text-[11px] font-mono text-slate-600" data-testid={`file-version-${file.id}`}>v{file.current_version}</span>
            <IntegrityBadge status={file.integrity?.status} testId={`file-integrity-${file.id}`} />
            {file.status === "deleted" && <span className="text-[11px] text-red-600 font-semibold">Deleted</span>}
          </div>
          {file.linked_entity?.type && (
            <div className="text-[11px] text-slate-500 mt-1 truncate">
              Linked to {file.linked_entity.type}: <span className="font-mono">{file.linked_entity.id}</span>
            </div>
          )}
          <div className="text-[10px] text-slate-400 font-mono mt-1 truncate" title={versions[0]?.sha256}>
            sha256 {versions[0]?.sha256?.slice(0, 20)}…
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-3 flex-wrap">
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => downloadVersion(file.id, versions[0])}
                disabled={!versions[0] || versions[0].deleted} data-testid={`file-download-${file.id}`}>
          <Download className="w-3.5 h-3.5 mr-1" /> Latest
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={verify} disabled={verifying}
                data-testid={`file-verify-${file.id}`}>
          <ShieldCheck className="w-3.5 h-3.5 mr-1" /> {verifying ? "Checking…" : "Verify"}
        </Button>
        {auth.can("files", "edit") && file.status !== "deleted" && (
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setUpload(true)}
                  data-testid={`file-new-version-${file.id}`}>
            <Upload className="w-3.5 h-3.5 mr-1" /> New version
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-8 text-xs ml-auto" onClick={() => setShowVersions((s) => !s)}
                data-testid={`file-toggle-versions-${file.id}`}>
          <History className="w-3.5 h-3.5 mr-1" /> {versions.length} version{versions.length === 1 ? "" : "s"}
        </Button>
      </div>
      {showVersions && (
        <ul className="mt-3 border-t border-slate-100 pt-3 space-y-2" data-testid={`file-versions-${file.id}`}>
          {versions.map((v) => (
            <li key={v.version} className="flex items-center gap-3 text-[11px]" data-testid={`file-version-row-${file.id}-${v.version}`}>
              <span className="font-mono font-semibold text-[#003366] w-7">v{v.version}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-slate-700">{v.filename} · {fmtBytes(v.size)}{v.deleted ? " · deleted" : ""}</span>
                <span className="block text-slate-400">{v.uploaded_by} · {fmtDT(v.uploaded_at)}{v.note ? ` · ${v.note}` : ""}</span>
                <span className="block font-mono text-slate-400 truncate">{v.sha256}</span>
              </span>
              {!v.deleted && (
                <button onClick={() => downloadVersion(file.id, v)} className="text-[#008080] hover:underline"
                        data-testid={`file-version-download-${file.id}-${v.version}`}>
                  Download
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <FileUploadDialog open={upload} onOpenChange={setUpload} mode="version" fileObject={file} onUploaded={() => onChanged?.()} />
    </div>
  );
};
